import { memo, useMemo, useState } from 'react';
import cn from 'clsx';
import { useAtomValue } from 'jotai';
import { Link, useLocation } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { hlFilteredPairsAtom, getEventResult, type EventKol } from 'r/atoms';
import type { Analyse, CryptoPair, SanseiEvent, SavedTweet } from 'r/types';
import { MultiSelectComboBox, SpinnerCircle } from 'r/ui';
import type { MultiSelectOption } from 'r/ui/MultiSelectComboBox';
import { dayjs } from 'r/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import PaginationControls from '../../AnalysisManagerTab/PaginationControls';
import { DirectionBadge, EventResultBadge } from '../shared';
import {
  useAnalysesByTweetIDs,
  useCreateAnalysisForTweet,
  useKolTweets,
} from './queries';
import useOpenPlayEvent from './useOpenPlayEvent';

type TweetState = 'event' | 'analysis' | 'new';

const stateConfig: Record<TweetState, { label: string; className: string }> = {
  event: {
    label: 'Event',
    className: 'border-l-purple-500',
  },
  analysis: {
    label: 'Analysis',
    className: 'border-l-yellow-500',
  },
  new: {
    label: 'New',
    className: 'border-l-blue-500',
  },
};

const CASHTAG_REGEX = /\$([A-Za-z][A-Za-z0-9]{1,14})\b/g;

/** Suggests pairs from cashtags in the tweet ($HYPE -> HYPE/USD) */
const suggestPairs = (text: string, pairs: CryptoPair[]): CryptoPair[] => {
  const tags = new Set(
    [...text.matchAll(CASHTAG_REGEX)].map((m) => m[1]!.toUpperCase()),
  );
  return pairs.filter((p) => tags.has(p.symbol.split('/')[0]!.toUpperCase()));
};

export default function KolTweets({ kol }: { kol: EventKol }) {
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [hideProcessed, setHideProcessed] = useState(false);

  const tweetsQuery = useKolTweets(kol.kolID, page, limit);
  const tweets = tweetsQuery.data?.data ?? [];
  const total = tweetsQuery.data?.total ?? 0;

  const tweetIDs = useMemo(() => tweets.map((t) => t.tweetID), [tweets]);
  const analysesQuery = useAnalysesByTweetIDs(tweetIDs);

  const analysesByTweet = useMemo(() => {
    const map = new Map<string, Analyse[]>();
    for (const analysis of analysesQuery.data?.data ?? []) {
      const list = map.get(analysis.tweetID);
      if (list) list.push(analysis);
      else map.set(analysis.tweetID, [analysis]);
    }
    return map;
  }, [analysesQuery.data]);

  const eventsByTweet = useMemo(
    () => new Map(kol.events.map((e) => [e.tweetID, e])),
    [kol.events],
  );

  const getState = (tweet: SavedTweet): TweetState =>
    eventsByTweet.has(tweet.tweetID) ? 'event'
    : analysesByTweet.has(tweet.tweetID) ? 'analysis'
    : 'new';

  const visibleTweets =
    hideProcessed ? tweets.filter((t) => getState(t) === 'new') : tweets;

  const counts = tweets.reduce(
    (acc, t) => {
      acc[getState(t)]++;
      return acc;
    },
    { event: 0, analysis: 0, new: 0 } as Record<TweetState, number>,
  );

  if (tweetsQuery.isLoading) {
    return (
      <div className="flex justify-center p-10">
        <SpinnerCircle size={24} color="var(--muted-foreground)" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-secondary font-medium">
          {total} tweets in CMS · on this page: {counts.new} new,{' '}
          {counts.analysis} with analysis, {counts.event} with event
          {(tweetsQuery.isFetching || analysesQuery.isFetching) && (
            <span className="ml-2 text-blue-500 animate-pulse">
              Updating...
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="kol-tweets-hide-processed"
            checked={hideProcessed}
            onCheckedChange={(checked) => setHideProcessed(checked === true)}
          />
          <Label
            htmlFor="kol-tweets-hide-processed"
            className="text-sm text-secondary"
          >
            Only new on this page
          </Label>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {visibleTweets.map((tweet) => (
          <TweetRow
            key={tweet.id}
            tweet={tweet}
            state={getState(tweet)}
            analyses={analysesByTweet.get(tweet.tweetID)}
            event={eventsByTweet.get(tweet.tweetID)}
            isAnalysesLoading={analysesQuery.isLoading}
          />
        ))}
        {visibleTweets.length === 0 && (
          <div className="py-8 text-center text-muted-foreground border-2 border-dashed border-bg-accent-tabs rounded-xl">
            {tweets.length === 0 ?
              'No tweets stored in CMS for this author'
            : 'All tweets on this page are processed'}
          </div>
        )}
      </div>

      <PaginationControls
        key={page}
        page={page}
        total={total}
        limit={limit}
        limitOptions={[20, 50, 100]}
        onPageChange={setPage}
        onLimitChange={(value) => {
          setLimit(value);
          setPage(0);
        }}
      />
    </div>
  );
}

const TweetRow = memo(function TweetRow({
  tweet,
  state,
  analyses,
  event,
  isAnalysesLoading,
}: {
  tweet: SavedTweet;
  state: TweetState;
  analyses: Analyse[] | undefined;
  event: SanseiEvent | undefined;
  isAnalysesLoading: boolean;
}) {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const text = tweet.tweetText ?? '';
  const isLong = text.length > 280;

  return (
    <div
      className={cn(
        'bg-main rounded-xl border border-bg-accent-tabs border-l-4 p-4 flex flex-col gap-3',
        stateConfig[state].className,
        tweet.deletedFromTweeterAt && 'opacity-60',
      )}
    >
      <div className="flex flex-wrap items-center gap-2 text-xs text-secondary">
        <span className="font-semibold text-main">
          {stateConfig[state].label}
        </span>
        <span>{dayjs(tweet.tweetDate).format('DD.MM.YYYY HH:mm')}</span>
        {tweet.repostOfTweetID && (
          <span className="px-2 py-0.5 bg-gray-500/10 rounded-full">
            Repost
          </span>
        )}
        {tweet.deletedFromTweeterAt && (
          <span className="px-2 py-0.5 bg-red-500/10 text-red-500 rounded-full">
            Deleted from X
          </span>
        )}
        <a
          href={`https://x.com/${tweet.authorUsername}/status/${tweet.tweetID}`}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto inline-flex items-center gap-1 text-focus hover:underline"
        >
          Open on X <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      <p className="text-sm text-main leading-relaxed whitespace-pre-line">
        {isLong && !isExpanded ? `${text.slice(0, 280)}...` : text}
        {isLong && (
          <button
            onClick={() => setIsExpanded((v) => !v)}
            className="text-focus ml-1 font-medium cursor-pointer hover:underline"
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </p>

      {tweet.images?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tweet.images.map((src) => (
            <a key={src} href={src} target="_blank" rel="noopener noreferrer">
              <img
                src={src}
                alt=""
                loading="lazy"
                className="max-h-20 rounded hover:opacity-75 transition-opacity"
              />
            </a>
          ))}
        </div>
      )}

      <div className="pt-3 border-t border-bg-accent-tabs flex flex-col gap-2">
        {event && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-medium text-main truncate max-w-80">
              {event.title}
            </span>
            <span className="text-secondary">{event.symbol}</span>
            <DirectionBadge isUp={event.isDirectionUp} />
            <EventResultBadge result={getEventResult(event)} />
            <Link
              to={`/events/${event.id}/edit`}
              state={{ background: location }}
              className="ml-auto text-focus hover:underline"
            >
              Open event
            </Link>
          </div>
        )}
        {!event &&
          analyses?.map((analysis) => (
            <AnalysisLine key={analysis.id} analysis={analysis} />
          ))}
        {state === 'new' &&
          (isAnalysesLoading ?
            <span className="text-xs text-secondary">Checking analyses...</span>
          : <CreateAnalysisForm tweet={tweet} />)}
      </div>
    </div>
  );
});

function AnalysisLine({ analysis }: { analysis: Analyse }) {
  const location = useLocation();
  const { openPlayEvent, isPending } = useOpenPlayEvent();

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="px-2 py-0.5 bg-bg-accent-main rounded-md border border-bg-accent-tabs font-semibold">
        {analysis.pair}
      </span>
      <DirectionBadge isUp={analysis.isPredictionUp} />
      {analysis.verifiedBy && <span className="text-yellow-600">Verified</span>}
      <div className="ml-auto flex gap-2">
        <Button variant="outline" size="sm" asChild>
          <Link
            to={`/analyses/${analysis.id}/edit`}
            state={{ background: location }}
          >
            Edit
          </Link>
        </Button>
        <Button
          size="sm"
          onClick={() => openPlayEvent(analysis)}
          disabled={isPending}
        >
          Create event
        </Button>
      </div>
    </div>
  );
}

function CreateAnalysisForm({ tweet }: { tweet: SavedTweet }) {
  const pairs = useAtomValue(hlFilteredPairsAtom);
  const { mutate: createAnalysis, isPending } = useCreateAnalysisForTweet();

  const suggested = useMemo(
    () => suggestPairs(tweet.tweetText ?? '', pairs),
    [tweet.tweetText, pairs],
  );
  const pairOptions: MultiSelectOption[] = useMemo(
    () =>
      pairs.map((p) => ({ id: p.symbol, label: p.symbol, icon: p.logoUrl })),
    [pairs],
  );

  const [selectedPair, setSelectedPair] = useState<MultiSelectOption[]>([]);
  const [direction, setDirection] = useState<'up' | 'down' | null>(null);

  const pair = selectedPair[0]?.id;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {suggested.map((p) => (
        <button
          key={p.symbol}
          type="button"
          onClick={() =>
            setSelectedPair([
              { id: p.symbol, label: p.symbol, icon: p.logoUrl },
            ])
          }
          className={cn(
            'text-xs rounded-md px-2 py-1 font-medium transition-colors cursor-pointer',
            pair === p.symbol ?
              'bg-primary text-primary-foreground'
            : 'bg-muted hover:bg-muted/70',
          )}
          title="Pair from cashtag in tweet"
        >
          {p.symbol}
        </button>
      ))}
      <MultiSelectComboBox
        label="Pair"
        placeholder="Select pair"
        searchPlaceholder="Search pairs..."
        selectedItems={selectedPair}
        items={pairOptions}
        onSelectionChange={setSelectedPair}
        isDisabled={isPending}
        fuseKeys={['label']}
        single
        hideLabel
        width={200}
      />
      <div className="flex gap-1">
        {(['up', 'down'] as const).map((value) => (
          <Button
            key={value}
            size="sm"
            variant={direction === value ? 'default' : 'outline'}
            onClick={() => setDirection(value)}
            disabled={isPending}
          >
            {value === 'up' ? 'Up' : 'Down'}
          </Button>
        ))}
      </div>
      <Button
        size="sm"
        className="ml-auto"
        disabled={!pair || !direction || isPending}
        onClick={() =>
          pair &&
          createAnalysis({
            tweetID: tweet.tweetID,
            pair,
            isPredictionUp: direction === 'up',
          })
        }
      >
        {isPending ? 'Saving...' : 'Create analysis'}
      </Button>
    </div>
  );
}
